import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router";

import { useMutation, useQuery } from "@tanstack/react-query";
import { CircleAlertIcon, PlusIcon, SheetIcon, UnlinkIcon } from "lucide-react";

import { formatDate, formatDateAt } from "simplydo/core/dates";
import { asMutation, initUseQuery } from "simplydo/core/query";
import SearchParams from "simplydo/core/searchParams";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";
import {
  Table,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

type GetConnectionsRequest =
  OpenAPI.GET<"/connections/{entity}/{service}/{context_id}">;
type ConnectedEntity = GetConnectionsRequest["response"]["entities"][number] & {
  connection?: GetConnectionsRequest["response"]["entities"][number]["connection"] & {
    ownerUser?: OpenAPI.Schemas["PreparedUser"];
  };
};
type OnConnectCallback = (refreshCallback?: () => void) => void;
type OnRemoveConnectionResponse =
  OpenAPI.DELETE<"/connections/{entity}/{service}/{context_id}/{entity_id}">["response"];

export const useConnectedEntities = (
  entity: string,
  service: string,
  contextId: string,
  onConnect: OnConnectCallback = () => {},
  onDisconnect: (
    userId: string,
    response: OnRemoveConnectionResponse,
  ) => void = () => {},
  onConnectionError: (errorMessage: string) => void = () => {},
): {
  loading: boolean;
  errorMessage: string;
  connectedEntities: ConnectedEntity[];
  removeConnection: (entityId: string) => void;
  isDisconnectingEntityId: string | undefined;
} => {
  const [errorMessage, setErrorMessage] = useState("");
  const { search } = useLocation();
  const navigate = useNavigate();

  const connectedEntityQuery = useQuery(
    initUseQuery(api.connections.getConnectedEntitiesForContext, [
      entity,
      service,
      contextId,
    ]),
  );

  const removeConnectionMutation = useMutation({
    mutationKey: ["connections", "remove", entity, service, contextId],
    mutationFn: asMutation(api.connections.removeConnection)!,
    onSuccess: (params, variables) => {
      onDisconnect(variables[3] as string, params);
      toast.success("Connection removed");
      api.invalidateQueries(api.connections.getConnectedEntitiesForContext, [
        entity,
        service,
        contextId,
      ]);
    },
  });

  const removeConnection = useCallback(
    (entityId: string) => {
      util
        .confirm(
          "Remove this connection?",
          "Are you sure you want to remove this connection? The selected user will immediately lose access to this resource.",
        )
        .then(() => {
          removeConnectionMutation.mutate([
            entity,
            service,
            contextId,
            entityId,
          ]);
        })
        .catch(() => {});
    },
    [entity, service, contextId, removeConnectionMutation],
  );

  useEffect(() => {
    const params = new SearchParams(search);
    const connectionSuccessString = params.get("connectionSuccess");
    const connectionService = params.get("connectionService");
    // We only show toasts / updates if this URL mutation is for our service
    if (connectionService !== service) return;
    if (connectionSuccessString) {
      if (connectionSuccessString === "false") {
        const nextErrorMessage =
          params.get("message") ||
          "Unable to connect to your service at this time.";
        setErrorMessage(nextErrorMessage);
        onConnectionError(nextErrorMessage);
      } else if (connectionSuccessString === "true") {
        onConnect(() => {
          connectedEntityQuery.refetch();
        });
      }

      params.delete("connectionSuccess");
      params.delete("connectionType");
      params.delete("connectionService");
      params.delete("message");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [
    connectedEntityQuery,
    onConnect,
    onConnectionError,
    navigate,
    search,
    service,
  ]);

  return {
    loading: connectedEntityQuery.isLoading,
    errorMessage,
    connectedEntities: (connectedEntityQuery.data?.entities ||
      []) as ConnectedEntity[],
    removeConnection,
    isDisconnectingEntityId: removeConnectionMutation.variables?.[3],
  };
};

const ConnectionsManagerContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
  > p,
  h3,
  ${Table} {
    margin: 0 !important;
  }
`;

type ConnectionsManagerProps = {
  entity: string;
  entityId: string;
  serviceProvider: "microsoft" | "salesforce";
  service: string;
  contextId: string;
  containerStyle?: CSSProperties;
  connectionButtonText: string;
  title?: string;
  subtitle?: string;
  establishConnection?: () => void;
  onConnect?: OnConnectCallback;
  onDisconnect?: (
    entityId: string,
    response: OnRemoveConnectionResponse,
  ) => void;
  readOnly?: boolean;
  warningMessage?: string;
  buttons?: React.ReactNode;
  buttonSize?: "small" | "medium" | "large";
};

const ConnectionsManager = ({
  entity,
  entityId,
  service,
  serviceProvider,
  contextId,
  containerStyle = {},
  title,
  subtitle,
  connectionButtonText,
  onConnect,
  onDisconnect,
  readOnly = false,
  warningMessage,
  buttons,
  buttonSize = "medium",
}: ConnectionsManagerProps) => {
  const {
    loading,
    errorMessage,
    connectedEntities,
    removeConnection,
    isDisconnectingEntityId,
  } = useConnectedEntities(entity, service, contextId, onConnect, onDisconnect);
  const user = useAppSelector((state) => state.user);
  const [isConnecting, setIsConnecting] = useState(false);

  const currentEntityIsConnected = useMemo(
    () =>
      connectedEntities?.some(
        (connectedEntity) => connectedEntity._id === entityId,
      ),
    [connectedEntities, entityId],
  );

  if (!user) {
    return null;
  }

  return (
    <ConnectionsManagerContainer style={containerStyle}>
      {title ? (
        <H3>
          {title}
          {subtitle && <Subheader>{subtitle}</Subheader>}
        </H3>
      ) : (
        subtitle && <p>{subtitle}</p>
      )}
      {loading ? (
        <Placeholder>
          <PlaceholderLine style={{ height: 35 }} />
          <PlaceholderLine style={{ height: 15 }} />
        </Placeholder>
      ) : null}
      {!loading && warningMessage && !connectedEntities?.length ? (
        <Message color="orange">
          <MessageContent>
            <Icon icon={CircleAlertIcon} /> {warningMessage}
          </MessageContent>
        </Message>
      ) : null}
      {!loading && errorMessage ? (
        <Message color="red">
          <MessageHeader>Could not get connections</MessageHeader>
          <MessageContent>{errorMessage}</MessageContent>
        </Message>
      ) : null}
      {connectedEntities?.length ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>User</TableHeaderCell>
              <TableHeaderCell>Connected At</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          {connectedEntities.map((connectedEntity) => (
            <TableRow key={connectedEntity._id}>
              <TableCell>
                <UserChip user={connectedEntity.connection?.ownerUser} />
              </TableCell>
              <TableCell>
                {formatDateAt(connectedEntity?.connection?.generatedAt)}
              </TableCell>
              <TableCell
                style={{
                  display: "flex",
                  gap: "5px",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {connectedEntity.connection?.driveItemUrl &&
                connectedEntity._id === user._id ? (
                  <ButtonLink
                    disabled={readOnly}
                    color="secondary"
                    to={connectedEntity.connection.driveItemUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon={SheetIcon} /> Open{" "}
                    {connectedEntity.connection?.driveItemName || "Excel file"}
                  </ButtonLink>
                ) : null}
                <Button
                  disabled={
                    readOnly ||
                    (entity === "user" && connectedEntity._id !== user._id)
                  }
                  tooltip={
                    entity === "user" && connectedEntity._id !== user._id
                      ? "You cannot remove connections for other users"
                      : "Remove connection"
                  }
                  loading={isDisconnectingEntityId === connectedEntity._id}
                  onClick={() => removeConnection(connectedEntity._id!)}
                  variant="outline"
                >
                  <Icon icon={UnlinkIcon} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      ) : null}
      <div>
        {!loading && !currentEntityIsConnected ? (
          <Button
            disabled={readOnly}
            loading={isConnecting}
            onClick={() => {
              setIsConnecting(true);
              if (user) {
                util.connectOAuth(
                  serviceProvider,
                  user,
                  entity,
                  entityId,
                  contextId,
                  service,
                );
              }
            }}
            style={{ maxWidth: "fit-content" }}
            color="primary"
            size={buttonSize}
          >
            <Icon icon={PlusIcon} />
            {connectionButtonText}
          </Button>
        ) : null}
        {buttons}
      </div>
    </ConnectionsManagerContainer>
  );
};

export default ConnectionsManager;
